import type { ListBuilderConfiguration } from '../types';

/**
 * Item Warehouse Item Embedded list configuration
 * Shows warehouse locations for a specific item
 * Features secondary headers for additional column info
 * Converted from JSON to TypeScript for type safety
 */
export const ITEM_WAREHOUSE_ITEM_EMBEDDED_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-warehouses-item-embedded',
			cacheTTL: 600000,
			name: 'Warehouse Locations',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'warehouse-info',
					fields: [
						{
							id: 'warehouse-name-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'warehouseName' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'lb1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'warehouse-address-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'warehouseAddress' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [
						{ id: 'h1', text: 'Warehouse' },
						{ id: 'h1-sub', text: 'Address', type: 'secondary' },
					],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'warehouseName' }],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'middle',
				},
				{
					id: 'external-id',
					fields: [
						{
							id: 'external-id-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'externalId' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: 'External ID' }],
					minWidth: 120,
					sortable: true,
					alignment: 'left',
					sortFields: [{ direction: 'ASC', fieldName: 'externalId' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-on-hand',
					fields: [
						{
							id: 'qty-on-hand-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityOnHand' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
						{
							id: 'lb2',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'expiration-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'soonestExpirationDate' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'date',
						},
					],
					headers: [
						{ id: 'h3', text: 'Qty On Hand' },
						{ id: 'h3-sub', text: 'Soonest Expiration', type: 'secondary' },
					],
					minWidth: 130,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'quantityOnHand' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'qty-on-order',
					fields: [
						{
							id: 'qty-on-order-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'quantityOnOrder' },
								defaultValue: '0',
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'number',
						},
						{
							id: 'lb3',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'arrival-date-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'soonestArrivalDate' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'date',
						},
					],
					headers: [
						{ id: 'h4', text: 'Qty On Order' },
						{ id: 'h4-sub', text: 'Soonest Arrival', type: 'secondary' },
					],
					minWidth: 130,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'quantityOnOrder' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'pricing',
					fields: [
						{
							id: 'price-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'price' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'lb4',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'min-price-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'minimumPrice' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h5', text: 'Price' },
						{ id: 'h5-sub', text: 'Minimum Price', type: 'secondary' },
					],
					minWidth: 120,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'price' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
				{
					id: 'costs',
					fields: [
						{
							id: 'acquisition-cost-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'acquisitionCost' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'currency',
						},
						{
							id: 'lb5',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'line_break',
						},
						{
							id: 'sell-cost-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'sellCost' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'currency',
						},
					],
					headers: [
						{ id: 'h6', text: 'Acquisition Cost' },
						{ id: 'h6-sub', text: 'Sell Cost', type: 'secondary' },
					],
					minWidth: 140,
					sortable: true,
					alignment: 'right',
					sortFields: [{ direction: 'DESC', fieldName: 'acquisitionCost' }],
					widthConfig: { mode: 'flexible', preset: 'medium' },
					verticalAlignment: 'middle',
				},
			],
			description: 'Warehouses for this item',
			defaultSortId: 'column-warehouse-info-asc',
			requiredFields: [
				'itemWarehouse.id',
				'itemWarehouse.itemId',
				'itemWarehouse.warehouseId',
				'itemWarehouse.quantityOnHand',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: ['itemWarehouseKeywords'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/warehouses/:warehouseId',
			objectLabel: 'Item Warehouse',
			objectTable: 'item_warehouse',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'externalId', fieldLabel: 'External ID' },
				{ fieldName: 'quantityOnHand', fieldLabel: 'Quantity On Hand' },
				{ fieldName: 'quantityOnOrder', fieldLabel: 'Quantity On Order' },
				{ fieldName: 'price', fieldLabel: 'Price' },
				{ fieldName: 'minimumPrice', fieldLabel: 'Minimum Price' },
				{ fieldName: 'acquisitionCost', fieldLabel: 'Acquisition Cost' },
				{ fieldName: 'sellCost', fieldLabel: 'Sell Cost' },
				{
					fieldName: 'soonestExpirationDate',
					fieldLabel: 'Soonest Expiration Date',
				},
				{ fieldName: 'soonestArrivalDate', fieldLabel: 'Soonest Arrival Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'warehouse-name',
			name: 'Warehouse Name',
			fields: [{ direction: 'ASC', fieldName: 'warehouseName' }],
			description: 'Sort by warehouse name',
		},
		{
			id: 'qty-on-hand',
			name: 'Quantity On Hand',
			fields: [{ direction: 'DESC', fieldName: 'quantityOnHand' }],
			description: 'Sort by quantity on hand (highest first)',
		},
		{
			id: 'expiration-date',
			name: 'Expiration Date',
			fields: [{ direction: 'ASC', fieldName: 'soonestExpirationDate' }],
			description: 'Sort by soonest expiration date',
		},
		{
			id: 'price',
			name: 'Price',
			fields: [{ direction: 'DESC', fieldName: 'price' }],
			description: 'Sort by price (highest first)',
		},
	],
};
